import { IFareTypeState, IFaresState } from '../../reducers/fares';
import {
  IFlexiTicketSummary,
  ISearchTicketResult,
  ITicketProductWithBundleType,
} from '@nx-ng/types';
import {
  ITicketsAndFareState,
  getTicketAndFaresFeatureState,
} from '../../reducers';

import { FareRequest, GetFareResponse } from '../../client/src';
import { createFlexiSummary } from './util/create-flexi-summary';
import { createSelector } from '@ngrx/store';
import { formatFaresWithBundleType } from './util/format-fares-with-bundle-type';
import { selectIdAndDescriptionsFromFares } from './util/select-fare-descriptions';
import { selectSelectedValues, selectSelectionsValid } from '../fare-selectors';
import { selectSearchFares } from './util/select-search-fares';
import { LoadingDetails } from '@nx-ng/types/loading-details.interface';
import { getLoadingDetails } from '@nx-ng/util/get-loading-details';

export const selectFaresState = createSelector(
  getTicketAndFaresFeatureState,
  (state: ITicketsAndFareState) => state?.fares
);

export const selectFlexifaresState = createSelector(
  selectFaresState,
  (state: IFaresState): IFareTypeState => state?.flexi
);

export const selectFlexiFaresLoadState = createSelector(
  selectFlexifaresState,
  (state: IFareTypeState) => state?.loadState
);

export const selectFlexiFaresErrors = createSelector(
  selectFlexifaresState,
  (state: IFareTypeState) => state?.errors
);

export const selectAvailableFlexiFares = createSelector(
  selectFlexifaresState,
  (state: IFareTypeState) => state?.fares
);

export const selectFlexiFaresLoading = createSelector(
  selectFlexifaresState,
  (state: IFareTypeState): LoadingDetails =>
    getLoadingDetails(state?.loadState, 'loading_flexible_tickets')
);

export const selectAvailableFlexiSummary = createSelector(
  selectAvailableFlexiFares,
  (state?: GetFareResponse): IFlexiTicketSummary[] => createFlexiSummary(state)
);

export const selectPaygfaresState = createSelector(
  selectFaresState,
  (state: IFaresState): IFareTypeState => state?.payg
);

export const selectPaygFaresLoadState = createSelector(
  selectPaygfaresState,
  (state: IFareTypeState) => state?.loadState
);

export const selectPaygFaresErrors = createSelector(
  selectPaygfaresState,
  (state: IFareTypeState) => state?.errors
);

export const selectAvailablePaygFares = createSelector(
  selectPaygfaresState,
  (state: IFareTypeState) => state?.fares
);

export const selectPaygFaresLoading = createSelector(
  selectPaygfaresState,
  (state: IFareTypeState): LoadingDetails =>
    getLoadingDetails(state?.loadState, 'loading_payg_fares')
);

export const selectAvailablePaygSummary = createSelector(
  selectAvailablePaygFares,
  (state?: GetFareResponse): IFlexiTicketSummary[] => createFlexiSummary(state)
);

export const selectSearchFaresState = createSelector(
  selectFaresState,
  (state: IFaresState): IFareTypeState => state?.search
);

export const selectSearchFaresLoading = createSelector(
  selectSearchFaresState,
  (state: IFareTypeState): LoadingDetails =>
    getLoadingDetails(state?.loadState, 'loading_ticket_results')
);

export const selectAvailableSearchFaresState = createSelector(
  selectSearchFaresState,
  (state: IFareTypeState) => state?.fares
);

export const selectSearchFaresMatchingSelections = createSelector(
  selectSelectionsValid,
  selectAvailableSearchFaresState,
  selectSelectedValues,
  (
    valid: boolean,
    fares?: GetFareResponse | undefined,
    values?: Partial<FareRequest>
  ): ISearchTicketResult[] => {
    return selectSearchFares({
      fares,
      values,
      valid,
    });
  }
);

export const selectAllFares = createSelector(
  selectFaresState,
  (fares: IFaresState): ITicketProductWithBundleType[] => {
    return formatFaresWithBundleType(fares);
  }
);

export const selectFareIdsAndDescriptions = createSelector(
  selectAllFares,
  (allFares: ITicketProductWithBundleType[]) => {
    return selectIdAndDescriptionsFromFares(allFares);
  }
);
